import React, { useCallback, useMemo, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { isString } from 'lodash';
import { setEntityFilters } from '@actions/entities-actions';
import { setMapFilterRadio, toggleMapFilterToggle } from '@actions/map-actions';
import FilterGroup from '@components/map/tray/filter/shared/filter-group';
import FilterSearch from '@components/map/tray/filter/shared/filter-search';
import FilterAttrSearch from '@components/map/tray/filter/shared/filter-attr-search';
import { getFiltersMap } from '@constants/config';
import { blankIconStyles } from '@constants/mui-theme';
import EntityTypeIcon from '@icons/entity-type-icon';
import { entityFiltersSelector } from '@selectors/entities-selector';
import FilterAvatar from '@shared/filter-avatar';
import { categoryToInt } from '@utils/filter-utils';
import {
  getAgencyIcon,
  getEntityLegend,
  getPriorityIconUrl
} from '@utils/icon-utils';
import {
  getAgencyId,
  getUserId
} from '@utils/permission-utils';
import { enDash } from '@utils/shared-utils';
import BaseTrayFilter from './base-filter';
import OverlapsFilter from './overlaps-filter';
import './filter.scss';

const EntityFilter = ({ config }) => {
  const dispatch = useDispatch();
  const { type, sections } = config;

  // Selectors
  const filterEnabled = useSelector(state => state.map.filterToggles[type]);
  const filterRadios = useSelector(state => state.map.filterRadios[type]);
  const entityFilters = useSelector(state => entityFiltersSelector(state, { type }));
  const dataTypes = useSelector(state => state.dataTypes);
  const agency = dataTypes.agency;
  const user = dataTypes.user;

  const myAgencyName = R.pathOr(null, [getAgencyId(), 'name'], agency);
  const myUserName = R.pathOr(null, [getUserId(), 'name'], user);

  // Compute section data
  const sectionData = useMemo(() => {
    const data = {};
    sections.forEach(section => {
      if (section.type === 'data-type-group' || section.type === 'data-type-search') {
        const dataTypeData = R.values(dataTypes[section.data_type]);
        if (section.data_type !== 'map_category') {
          data[section.data_type] = dataTypeData;
        } else if (section.category) {
          const categoryTypes = dataTypes.map_category_type;
          if (categoryTypes) {
            const categoryType = parseInt(Object.keys(categoryTypes).find(key => categoryTypes[key].name === section.category), 10);
            const categories = Object.values(
              R.pickBy(category => category.type === categoryType && category.id > 0 && category.active, dataTypeData)
            );
            const blank = Object.values(
              R.pickBy(category => category.type === categoryType && isString(category.id), dataTypeData)
            ) || [];
            data[`category-${section.category}`] = [...blank, ...categories];
          }
        }
      }
    });
    return data;
  }, [dataTypes, sections]);

  const onToggleClick = useCallback(() => {
    dispatch(toggleMapFilterToggle(type));
  }, [dispatch, type]);

  const setFilters = useCallback(filters => {
    dispatch(setEntityFilters(type, filters));
  }, [dispatch, type]);

  const setFilterRadio = useCallback((event, value) => {
    dispatch(setMapFilterRadio(type, event, value));
  }, [dispatch, type]);

  const getItemIcon = (icon, alt) => <FilterAvatar src={icon} alt={alt} />;

  const getAgencyTypeIcon = useCallback(item => {
    const icon = getAgencyIcon(item);
    return getItemIcon(icon, item.name);
  }, []);

  const getCategoryIcon = useCallback(item => {
    const categoryId = categoryToInt(item.id);
    if (isString(categoryId)) {
      // Blank entries has no icons.
      return <div style={blankIconStyles}>{enDash}</div>;
    }
    const icon = getEntityLegend(null, type, categoryId);
    return getItemIcon(icon, item.name);
  }, [type]);

  const getPriorityIcon = useCallback(item => {
    const icon = getPriorityIconUrl(item.id);
    if (icon) {
      return getItemIcon(icon, item.name);
    }
    return null;
  }, []);

  const getFilterKey = useCallback(() => `${type}Filters`, [type]);

  const getItems = useCallback(section => {
    if (section.values) {
      return section.values;
    }
    if (section.type === 'data-type-group') {
      if (section.data_type === 'map_category') {
        return sectionData[`category-${section.category}`];
      }
      return sectionData[section.data_type];
    }
    return null;
  }, [sectionData]);

  const getGenericFilterName = useCallback(section => {
    if (section.type === 'data-type-group' || section.type === 'data-type-search' || section.type === 'entity-attr-search') {
      return getFiltersMap()[getFilterKey()][section.filter_name][0];
    }
    return null;
  }, [getFilterKey]);

  const getGenericFilterRadio = useCallback(section => {
    if (section.radio) {
      return {
        checked: filterRadios[section.radioType].checked,
        value: section.radioType,
        onCheck: setFilterRadio
      };
    }
    return null;
  }, [filterRadios, setFilterRadio]);

  const getGenericFilterEnabled = useCallback(section => {
    if (section.radio) {
      return filterRadios[section.radioType].checked && filterEnabled;
    }
    return filterEnabled;
  }, [filterEnabled, filterRadios]);

  const getFilterGroupIcon = useCallback(section => {
    if (section.icon_type === 'agency') {
      return getAgencyTypeIcon;
    }
    if (section.icon_type === 'category') {
      return getCategoryIcon;
    }
    if (section.icon_type === 'priority') {
      return getPriorityIcon;
    }
    return null;
  }, [getAgencyTypeIcon, getCategoryIcon, getPriorityIcon]);

  const getQuickSelect = useCallback(section => {
    if (section.data_type === 'agency') {
      return {
        label: myAgencyName,
        value: getAgencyId()
      };
    }
    if (section.data_type === 'user') {
      return {
        label: myUserName,
        value: getUserId()
      };
    }
    return null;
  }, [myAgencyName, myUserName]);

  const renderFilterGroup = useCallback(section => {
    const items = getItems(section);
    return (
      <FilterGroup
        enabled={getGenericFilterEnabled(section)}
        category={section.category}
        filterEnabled={filterEnabled}
        getIcon={getFilterGroupIcon(section)}
        filterName={getGenericFilterName(section)}
        items={items}
        setFilters={setFilters}
        negateIfEmpty={section.negateIfEmpty}
        radio={getGenericFilterRadio(section)}
        title={section.title}
        values={entityFilters}
      />
    );
  }, [entityFilters, filterEnabled, getFilterGroupIcon, getGenericFilterEnabled, getGenericFilterName, getGenericFilterRadio, getItems, setFilters]);

  const renderFilterSearch = useCallback(section => (
    <FilterSearch
      dataType={section.data_type}
      enabled={getGenericFilterEnabled(section)}
      filterName={getGenericFilterName(section)}
      filterEnabled={filterEnabled}
      filterFunc={item => {
        // If the data type is 'user',
        // filter them by agency.
        if (section.data_type === 'user') {
          const { agency__in } = entityFilters;
          const isInList = agencyId => {
            let list = null;
            if (Array.isArray(agency__in)) {
              list = agency__in;
            } else {
              list = agency__in.split(',').map(idStr => parseInt(idStr, 10));
            }
            return list.includes(agencyId);
          };
          if (agency__in && isInList(item.agency)) {
            return true;
          } else if (!agency__in) {
            return true;
          }
          return false;
        }
        // Else no filtering:
        return true;
      }}
      placeHolder={section.placeholder}
      quickSelect={getQuickSelect(section)}
      radio={getGenericFilterRadio(section)}
      setFilters={setFilters}
      title={section.title}
      values={entityFilters}
    />
  ), [entityFilters, filterEnabled, getGenericFilterEnabled, getGenericFilterName, getGenericFilterRadio, getQuickSelect, setFilters]);

  const renderAttrSearch = useCallback(section => (
    <FilterAttrSearch
      dataType={section.data_type}
      enabled={getGenericFilterEnabled(section)}
      filterName={getGenericFilterName(section)}
      filterEnabled={filterEnabled}
      placeHolder={section.placeholder}
      quickSelect={getQuickSelect(section)}
      radio={getGenericFilterRadio(section)}
      setFilters={setFilters}
      title={section.title}
      values={entityFilters}
      attrField={section.field}
    />
  ), [entityFilters, filterEnabled, getGenericFilterEnabled, getGenericFilterName, getGenericFilterRadio, getQuickSelect, setFilters]);

  const renderOverlapsFilter = useCallback(() => (
    <OverlapsFilter disabled={!filterEnabled} label={config.label} />
  ), [config.label, filterEnabled]);

  const renderIcon = useCallback(() => (
    <div styleName="icon">
      <EntityTypeIcon type={type} size="1.5rem" aria-label={config.label} />
    </div>
  ), [config.label, type]);

  const renderContent = useCallback(() => (
    <div styleName="content-filter-group-container">
      {sections.map((section, index) => {
        const key = section.filter_name || `${section.type}: ${index}`;
        if (section.type === 'separator') {
          return <div key={key} styleName="content-filter-group-divider" />;
        }
        if (section.type === 'data-type-group') {
          return <div key={key}>{renderFilterGroup(section)}</div>;
        }
        if (section.type === 'data-type-search') {
          return <div key={key}>{renderFilterSearch(section)}</div>;
        }
        if (section.type === 'data-type-overlap') {
          return <div key={key}>{renderOverlapsFilter()}</div>;
        }
        if (section.type === 'entity-attr-search') {
          return <div key={key}>{renderAttrSearch(section)}</div>;
        }
        return null;
      })}
    </div>
  ), [renderAttrSearch, renderFilterGroup, renderFilterSearch, renderOverlapsFilter, sections]);

  return (
    <BaseTrayFilter
      config={config}
      filterEnabled={filterEnabled}
      onToggleClick={onToggleClick}
      renderContent={renderContent}
      renderIcon={renderIcon}
    />
  );
};

EntityFilter.propTypes = {
  config: PropTypes.object
};

export default memo(EntityFilter);
